import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  pagerdutyAiAccessTokenPrefix,
  pagerdutyAiClientSecretSecretId,
  pagerdutyAiConnectorName,
} from "@p0security/integrations-pagerduty-ai-shared/secret-manager";
import type { PagerdutyAiComponents } from "@p0security/integrations-pagerduty-ai-shared/types";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  PagerdutyAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-pagerduty-ai-gcloud",
  digest:
    "sha-8870126@sha256:94d46fa49e33e6561cc2397fdbd27dc058eab58345afaf118169bd0177a1ee31",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-pagerduty-ai-aws",
  digest:
    "sha-51ed50d@sha256:8afcab1a03698e6c63b34a1f23db2907ca2157fc28479074f572605573e6da7f",
} as const;

const awsClientSecretSecretId = (account: string) =>
  secretProviderFor("aws").replacer(pagerdutyAiClientSecretSecretId(account));

export const awsStoreClientSecret = (
  account: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    `printf '%s' "$PD_CLIENT_SECRET"`,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsClientSecretSecretId(account)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: { resource: "pagerduty", label: "PagerDuty" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: pagerdutyAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      pagerdutyAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "client_secret",
      itemTfId: tfId(itemId),
      secretId: awsClientSecretSecretId(itemId),
      label: "client secret",
    },
  });

const gcpClientSecretSecretId = (account: string) =>
  secretProviderFor("gcp").replacer(pagerdutyAiClientSecretSecretId(account));

export const gcpStoreClientSecret = (
  account: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    `printf '%s' "$PD_CLIENT_SECRET"`,
    `| gcloud secrets versions add ${gcpClientSecretSecretId(account)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: { resource: "pagerduty", role: "Pagerduty", label: "PagerDuty" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      pagerdutyAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "client_secret",
      itemTfId: tfId(itemId),
      secretId: gcpClientSecretSecretId(itemId),
      label: "client secret",
    },
  });
